import Immutable from 'immutable';
import React from 'react';

import ParameterSet from 'sharedreact/components/ParameterSet';
import SearchField from 'sharedreact/components/SearchField';
import hideable from 'sharedreact/components/hideable';
import TableContainer from 'sharedreact/containers/TableContainer';
import SortOrder from 'sharedreact/data/SortOrder';
import TableColumn from 'sharedreact/data/TableColumn';
import User from 'sharedreact/data/User';
import type { RangeType } from 'sharedreact/stores/generic/Type';
import { translate } from 'sharedreact/utils/LanguageSupport';
import Dialog from 'sharedreact/components/Dialog';
import ValidationForm from 'sharedreact/components/ValidationForm';

const TABLE_NAME: string = 'UserSelectionTable';

function _isSelected (item: User, selection: Immutable.Set<string>): boolean {
  return selection.find(id => id === item.id || id === item.userInfoId) != null;
}

type Props = {
  show: boolean,
  language: string,
  title: string,
  users: Immutable.List<User | null>,
  filter: string,
  sortOrder: SortOrder,
  filterKeywords: string[],
  initialSelection: Immutable.Set<string>,
  selection: Immutable.Set<string>,
  ensureSelectionIsVisible: boolean,
  selectionIndexes: Immutable.Map<string, number>,
  selectedColumns?: Immutable.Set<string>,

  setVisibleRange: (range: RangeType) => void,
  setSelection: (selection: Immutable.Set<string>) => void,
  setFilter: (filterString: string) => void,
  setSortOrder: (sortOrder: SortOrder) => void,
  onItemSelect: (id: string) => void,
  onEnsuredSelectionIsVisible: () => void,

  onSelect: () => void,
  onClose: () => void,

  onInit: () => void
};

class UserSelectionDialog extends React.Component<Props> {
  constructor (props: Props) {
    super(props);


    this.handleSaveAndClose = this.handleSaveAndClose.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  showDialog (): void {
    this.props.setSelection(this.props.initialSelection);
  }

  override componentDidMount (): void {
    if (this.props.show) {
      this.showDialog();
      this.props.onInit();
    }
  }

  handleSaveAndClose () {
    this.props.onSelect();
    this.props.onClose();
  }

  handleClose () {
    this.props.onClose();
  }

  override render (): React.ReactNode {
    const { show, language, title, users,
      selection, sortOrder, ensureSelectionIsVisible,
      selectionIndexes, setFilter, filter, filterKeywords,
      selectedColumns, onItemSelect, setSortOrder,
      onEnsuredSelectionIsVisible, setVisibleRange,
     } = this.props;

    const tableColumns: Immutable.List<TableColumn<User>> = Immutable.List([{
      title: translate('common.user.name'),
      width: '33%',
      renderCell: (item: User, row: number, col: number) => {
        return (item.name ? item.name : '-');
      },
      sortOrderKeyword: 'name',
      selectable: true,
      movable: true,
      name: 'name'
    },
    {
      title: translate('common.user.fullname'),
      width: '67%',
      renderCell: (item: User, row: number, col: number) => {
        return (item.fullName ? item.fullName : '-');
      },
      sortOrderKeyword: 'fullname',
      selectable: true,
      movable: true,
      name: 'fullName'
    }]);

    const onOK = (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      this.handleSaveAndClose();
    };

    const enableOk: boolean = (
      selection.size > 0 &&
      (selectedColumns == null ||
       (selectedColumns.size > 0 &&
       tableColumns.filter(tableColumn => tableColumn.name != null && selectedColumns.includes(tableColumn.name)).size > 0))
    );

    return (
      <Dialog
        id='esko_dialog_userselection'
        show={show}
        language={language}
        title={title}
        onClose={this.handleClose}
        disableConfirm={!enableOk}
        onConfirm={onOK}
        closeText={translate('shared.general.cancel')}
        primaryButton='confirm'
      >
        <ValidationForm withProvider>
          <ParameterSet>
            <SearchField
              className='common_userselection-searchfield-inline'
              id={'ae_search_todo'}
              alignRight
              onSearch={setFilter}
              value={filter}
              keywords={filterKeywords}
              highlight={filter !== ''}
            />
            <TableContainer<User>
              className='common_userselection-tablecontainer'
              id='ae_userselection_table'
              tableName={TABLE_NAME}
              tableColumns={tableColumns}
              rowData={users}
              selection={selection}
              showSelection
              sortOrder={sortOrder}
              ensureSelectionIsVisible={ensureSelectionIsVisible}
              selectionIndexes={selectionIndexes}
              isSelected={_isSelected}
              setVisibleRange={setVisibleRange}
              onSortOrderChange={setSortOrder}
              onItemSelect={onItemSelect}
              onEnsuredSelectionIsVisible={onEnsuredSelectionIsVisible}
            />
          </ParameterSet>
        </ValidationForm>
      </Dialog>
    );
  }
}

export default hideable(UserSelectionDialog);
